import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// DEV_MODE: true in development (bypasses restrictions for testing), always false in production
const DEV_MODE = process.env.NODE_ENV !== 'production';

const SUBSCRIPTION_TIERS = {
  FREE: 'free',
  BASIC: 'basic',
  PRO: 'pro',
  ADVANCED: 'advanced',
  SUPER: 'super'
};

const TIER_CONFIG = {
  [SUBSCRIPTION_TIERS.FREE]: {
    name: 'Free',
    price: 0,
    period: null,
    displayPrice: 'Free',
    badge: null,
    features: {
      wizardGF: { maxStep: 3 },
      wizardA: { maxStep: 3 },
      wizardB: { maxStep: 3 },
      wizardC: { maxStep: 3 },
      wizardD: false,
      wizardE: false,
      wizardF: false,
      reportA: false,
      reportB: false,
      reportC: false,
      reportD: false,
      reportCombined: false,
      learnBasics: false,
      compareMethodology: false
    },
    description: 'Get started with basic research methodology guidance'
  },
  [SUBSCRIPTION_TIERS.BASIC]: {
    name: 'Basic User',
    price: 2,
    period: 'week',
    displayPrice: '$2/week',
    badge: null,
    features: {
      wizardGF: { maxStep: 999 },
      wizardA: { maxStep: 999 },
      wizardB: { maxStep: 3 },
      wizardC: { maxStep: 3 },
      wizardD: false,
      wizardE: false,
      wizardF: false,
      reportA: true,
      reportB: false,
      reportC: false,
      reportD: false,
      reportCombined: false,
      learnBasics: true,
      compareMethodology: false
    },
    description: 'Complete access to Research Methodology Wizard'
  },
  [SUBSCRIPTION_TIERS.PRO]: {
    name: 'Pro User',
    price: 4,
    period: 'month',
    displayPrice: '$4/month',
    badge: null,
    features: {
      wizardGF: { maxStep: 999 },
      wizardA: { maxStep: 999 },
      wizardB: { maxStep: 999 },
      wizardC: { maxStep: 999 },
      wizardD: false,
      wizardE: false,
      wizardF: false,
      reportA: true,
      reportB: true,
      reportC: true,
      reportD: false,
      reportCombined: false,
      learnBasics: true,
      compareMethodology: false
    },
    description: 'Full access to Methodology and Data Analysis'
  },
  [SUBSCRIPTION_TIERS.ADVANCED]: {
    name: 'Advanced User',
    price: 6,
    period: 'month',
    displayPrice: '$6/month',
    badge: 'Popular Choice',
    features: {
      wizardGF: { maxStep: 999 },
      wizardA: { maxStep: 999 },
      wizardB: { maxStep: 999 },
      wizardC: { maxStep: 999 },
      wizardD: { maxStep: 999 },
      wizardE: { maxStep: 999 },
      wizardF: { maxStep: 999 },
      reportA: true,
      reportB: true,
      reportC: true,
      reportD: true,
      reportE: true,
      reportF: true,
      reportCombined: true,
      learnBasics: true,
      compareMethodology: true
    },
    description: 'Complete research design toolkit with all features'
  },
  [SUBSCRIPTION_TIERS.SUPER]: {
    name: 'Super User',
    price: 60,
    period: 'year',
    displayPrice: '$60/year',
    originalPrice: '$72/year',
    discount: '50% discount',
    badge: 'Excellent Value',
    features: {
      wizardGF: { maxStep: 999 },
      wizardA: { maxStep: 999 },
      wizardB: { maxStep: 999 },
      wizardC: { maxStep: 999 },
      wizardD: { maxStep: 999 },
      wizardE: { maxStep: 999 },
      wizardF: { maxStep: 999 },
      reportA: true,
      reportB: true,
      reportC: true,
      reportD: true,
      reportE: true,
      reportF: true,
      reportCombined: true,
      learnBasics: true,
      compareMethodology: true
    },
    description: 'Best value - Full unlimited access for one year'
  }
};

const STORAGE_KEY = 'researchWizard_subscription';
const TRIALS_STORAGE_KEY = 'researchWizard_usedTrials';

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const [currentTier, setCurrentTier] = useState(SUBSCRIPTION_TIERS.FREE);
  const [subscriptionData, setSubscriptionData] = useState({
    tier: SUBSCRIPTION_TIERS.FREE,
    startDate: null,
    endDate: null,
    email: null
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalFeature, setUpgradeModalFeature] = useState(null);
  
  const [usedTrials, setUsedTrials] = useState([]);
  const [activeTrialFeature, setActiveTrialFeature] = useState(null);
  const isInTrialMode = activeTrialFeature !== null;

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const tier = parsed.startDate ? (parsed.tier || SUBSCRIPTION_TIERS.FREE) : SUBSCRIPTION_TIERS.FREE;
        setCurrentTier(tier);
        setSubscriptionData({ ...parsed, tier });
      } catch (e) {
        console.error('Failed to load subscription data:', e);
      }
    }
    
    const savedTrials = localStorage.getItem(TRIALS_STORAGE_KEY);
    if (savedTrials) {
      try {
        setUsedTrials(JSON.parse(savedTrials));
      } catch (e) {
        console.error('Failed to load trial data:', e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(subscriptionData));
  }, [subscriptionData]);
  
  useEffect(() => {
    localStorage.setItem(TRIALS_STORAGE_KEY, JSON.stringify(usedTrials));
  }, [usedTrials]);

  const getTierConfig = useCallback((tier = currentTier) => {
    return TIER_CONFIG[tier] || TIER_CONFIG[SUBSCRIPTION_TIERS.FREE];
  }, [currentTier]);

  const hasFeature = useCallback((feature) => {
    if (DEV_MODE) return true;
    if (isInTrialMode && activeTrialFeature === feature) {
      return true;
    }
    const config = getTierConfig();
    return config.features[feature] === true;
  }, [getTierConfig, isInTrialMode, activeTrialFeature]);

  const getWizardMaxStep = useCallback((wizard) => {
    if (DEV_MODE) return 999;
    if (isInTrialMode && activeTrialFeature === `wizard${wizard}`) {
      return 999;
    }
    const config = getTierConfig();
    const wizardKey = `wizard${wizard}`;
    const wizardConfig = config.features[wizardKey];
    
    if (wizardConfig === false) return -1;
    if (wizardConfig === true) return 999;
    if (typeof wizardConfig === 'object' && wizardConfig.maxStep !== undefined) {
      return wizardConfig.maxStep;
    }
    return 999;
  }, [getTierConfig, isInTrialMode, activeTrialFeature]);

  const canAccessWizard = useCallback((wizard) => {
    if (DEV_MODE) return true;
    if (isInTrialMode && activeTrialFeature === `wizard${wizard}`) {
      return true;
    }
    const maxStep = getWizardMaxStep(wizard);
    return maxStep >= 0;
  }, [getWizardMaxStep, isInTrialMode, activeTrialFeature]);

  const canAccessStep = useCallback((wizard, stepIndex) => {
    if (DEV_MODE) return true;
    if (isInTrialMode && activeTrialFeature === `wizard${wizard}`) {
      return true;
    }
    const maxStep = getWizardMaxStep(wizard);
    return stepIndex <= maxStep;
  }, [getWizardMaxStep, isInTrialMode, activeTrialFeature]);

  const canGenerateReport = useCallback((reportType) => {
    if (DEV_MODE) return true;
    const wizardKey = `wizard${reportType}`;
    if (isInTrialMode && activeTrialFeature === wizardKey) {
      return false;
    }
    const config = getTierConfig();
    const reportKey = `report${reportType}`;
    return config.features[reportKey] === true;
  }, [getTierConfig, isInTrialMode, activeTrialFeature]);
  
  const hasUsedTrial = useCallback((feature) => {
    return usedTrials.includes(feature);
  }, [usedTrials]);
  
  const canStartTrial = useCallback((feature) => {
    return !hasUsedTrial(feature) && !isInTrialMode;
  }, [hasUsedTrial, isInTrialMode]);
  
  const startTrial = useCallback((feature) => {
    if (!canStartTrial(feature)) return false;
    setActiveTrialFeature(feature);
    setUsedTrials(prev => [...prev, feature]);
    setShowUpgradeModal(false);
    setUpgradeModalFeature(null);
    return true;
  }, [canStartTrial]);
  
  const endTrial = useCallback(() => {
    setActiveTrialFeature(null);
  }, []);
  
  const canSaveDesign = useCallback(() => {
    return !isInTrialMode;
  }, [isInTrialMode]);

  const triggerUpgradeModal = useCallback((feature, requiredTier = null) => {
    setUpgradeModalFeature({ feature, requiredTier });
    setShowUpgradeModal(true);
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
    setUpgradeModalFeature(null);
  }, []);

  const upgradeTier = useCallback((newTier, email = null) => {
    const now = new Date();
    let endDate = new Date();
    
    const tierConfig = TIER_CONFIG[newTier];
    if (tierConfig.period === 'week') {
      endDate.setDate(endDate.getDate() + 7);
    } else if (tierConfig.period === 'month') {
      endDate.setMonth(endDate.getMonth() + 1);
    } else if (tierConfig.period === 'year') {
      endDate.setFullYear(endDate.getFullYear() + 1);
    }

    const newSubscription = {
      tier: newTier,
      startDate: now.toISOString(),
      endDate: tierConfig.period ? endDate.toISOString() : null,
      email: email || subscriptionData.email
    };

    setCurrentTier(newTier);
    setSubscriptionData(newSubscription);
    return newSubscription;
  }, [subscriptionData.email]);

  const resetToFree = useCallback(() => {
    const freeSubscription = {
      tier: SUBSCRIPTION_TIERS.FREE,
      startDate: null,
      endDate: null,
      email: null
    };
    setCurrentTier(SUBSCRIPTION_TIERS.FREE);
    setSubscriptionData(freeSubscription);
  }, []);

  const getRequiredTierForFeature = useCallback((feature) => {
    for (const [tier, config] of Object.entries(TIER_CONFIG)) {
      if (config.features[feature] === true || 
          (typeof config.features[feature] === 'object' && config.features[feature].maxStep > 3)) {
        return tier;
      }
    }
    return SUBSCRIPTION_TIERS.ADVANCED;
  }, []);

  const getAllTiers = useCallback(() => {
    return Object.entries(TIER_CONFIG).map(([id, config]) => ({
      id,
      name: config.name,
      price: config.price,
      displayPrice: config.displayPrice,
      period: config.period,
      badge: config.badge
    }));
  }, []);

  const value = {
    currentTier,
    subscriptionData,
    tiers: SUBSCRIPTION_TIERS,
    tierConfig: TIER_CONFIG,
    getTierConfig,
    hasFeature,
    getWizardMaxStep,
    canAccessWizard,
    canAccessStep,
    canGenerateReport,
    triggerUpgradeModal,
    closeUpgradeModal,
    showUpgradeModal,
    upgradeModalFeature,
    upgradeTier,
    resetToFree,
    getRequiredTierForFeature,
    isInTrialMode,
    activeTrialFeature,
    usedTrials,
    hasUsedTrial,
    canStartTrial,
    startTrial,
    endTrial,
    canSaveDesign,
    getAllTiers
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export { SUBSCRIPTION_TIERS, TIER_CONFIG };
